import { useEffect, useState } from "react";
import { Loader2, MapPin, Pencil, Plus, Store, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API_BASE } from "../../lib/api";

const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron";
const emptyForm = { agent_id: "", agent_name: "", shop_name: "", phone: "", address: "", state: "", district: "", city: "", pincode: "", lat: "", lng: "", photo_path: "" };

export default function AdminBranches() {
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/branches/admin").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/admin/customers?role=agent").then((r) => setAgents(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickAgent = (e) => {
    const id = e.target.value;
    const a = agents.find((x) => x.id === id);
    setForm({ ...form, agent_id: id, agent_name: a?.name || form.agent_name, phone: a?.phone || form.phone });
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/uploads", fd);
      setForm((f) => ({ ...f, photo_path: res.data.path }));
      toast.success("Shop photo uploaded");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, lat: form.lat === "" ? null : parseFloat(form.lat), lng: form.lng === "" ? null : parseFloat(form.lng) };
    try {
      if (editing) await api.put(`/branches/admin/${editing}`, payload);
      else await api.post("/branches/admin", payload);
      toast.success(editing ? "Branch updated" : "Branch added");
      setForm(emptyForm); setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const edit = (b) => {
    setEditing(b.id);
    setForm({ agent_id: b.agent_id || "", agent_name: b.agent_name, shop_name: b.shop_name, phone: b.phone,
              address: b.address, state: b.state, district: b.district, city: b.city, pincode: b.pincode || "",
              lat: b.lat ?? "", lng: b.lng ?? "", photo_path: b.photo_path || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (b) => {
    if (!window.confirm(`Delete branch "${b.shop_name}" (${b.city})?`)) return;
    try { await api.delete(`/branches/admin/${b.id}`); toast.success("Branch deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="admin-branches">
      <h1 className="font-heading text-2xl font-bold text-white">Branch Network</h1>
      <p className="mt-1 text-sm text-slate-500">Agents act as branches — they appear on the public Branches page grouped State → District → City.</p>

      <div className="mt-6 grid items-start gap-6 lg:grid-cols-3">
        <form onSubmit={save} className="rounded-xl border border-adminborder bg-admincard p-5">
          <p className="text-sm font-bold text-white">{editing ? "Edit Branch" : "Add Branch (Agent)"}</p>
          <div className="mt-3 space-y-2">
            <select className={darkInput} data-testid="branch-agent-select" value={form.agent_id} onChange={pickAgent}>
              <option value="">Select agent (optional)…</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}{a.phone ? ` · ${a.phone}` : ""}</option>)}
            </select>
            <input required className={darkInput} data-testid="branch-agent-name" placeholder="Agent name *" value={form.agent_name} onChange={set("agent_name")} />
            <input required className={darkInput} data-testid="branch-shop-name" placeholder="Shop name *" value={form.shop_name} onChange={set("shop_name")} />
            <input required className={darkInput} data-testid="branch-phone" placeholder="Phone *" value={form.phone} onChange={set("phone")} />
            <textarea required rows={2} className={darkInput} data-testid="branch-address" placeholder="Full shop address *" value={form.address} onChange={set("address")} />
            <div className="grid grid-cols-3 gap-2">
              <input required className={darkInput} data-testid="branch-state" placeholder="State *" value={form.state} onChange={set("state")} />
              <input required className={darkInput} data-testid="branch-district" placeholder="District *" value={form.district} onChange={set("district")} />
              <input required className={darkInput} data-testid="branch-city" placeholder="City *" value={form.city} onChange={set("city")} />
            </div>
            <input required maxLength={6} className={darkInput} data-testid="branch-pincode" placeholder="Pincode * (6 digits)" value={form.pincode} onChange={set("pincode")} />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" step="any" className={darkInput} data-testid="branch-lat" placeholder="Latitude (e.g. 17.3850)" value={form.lat} onChange={set("lat")} />
              <input type="number" step="any" className={darkInput} data-testid="branch-lng" placeholder="Longitude (e.g. 78.4867)" value={form.lng} onChange={set("lng")} />
            </div>
            <p className="text-[11px] text-slate-500">Lat/Lng powers the "Directions" button — find it on Google Maps → right-click the shop → copy coordinates.</p>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-adminborder px-3 py-2.5 text-xs font-semibold text-slate-400 hover:border-saffron hover:text-saffron" data-testid="branch-photo-upload">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {form.photo_path ? "Photo uploaded ✓ (click to replace)" : "Upload shop photo"}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
            </label>
            <div className="flex gap-2 pt-1">
              <button disabled={busy || uploading} data-testid="branch-save-btn"
                      className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {editing ? "Update Branch" : "Add Branch"}
              </button>
              {editing && (
                <button type="button" onClick={() => { setEditing(null); setForm(emptyForm); }} data-testid="branch-cancel-edit"
                        className="rounded-lg border border-adminborder px-3 py-2 text-xs text-slate-400">Cancel</button>
              )}
            </div>
          </div>
        </form>

        <div className="rounded-xl border border-adminborder bg-admincard lg:col-span-2">
          <p className="border-b border-adminborder px-5 py-3.5 text-sm font-bold text-white">All Branches ({branches.length})</p>
          <div className="grid gap-3 p-4 sm:grid-cols-2" data-testid="admin-branch-list">
            {branches.map((b) => (
              <div key={b.id} className="flex gap-3 rounded-xl border border-adminborder bg-adminbg p-3" data-testid={`admin-branch-${b.id}`}>
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-admincard">
                  {b.photo_path ? (
                    <img src={`${API_BASE}/api/branches/public/photo/${b.id}`} alt={b.shop_name} className="h-full w-full object-cover" />
                  ) : (
                    <Store className="m-4 h-8 w-8 text-slate-600" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white">{b.shop_name}</p>
                  <p className="text-[11px] text-saffron">{b.agent_name} · {b.phone}</p>
                  <p className="truncate text-[11px] text-slate-500">{b.city}, {b.district}, {b.state}{b.pincode ? ` — ${b.pincode}` : ""}</p>
                  {b.lat != null && <p className="flex items-center gap-1 text-[10px] text-emerald-400"><MapPin className="h-3 w-3" />{b.lat}, {b.lng}</p>}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button onClick={() => edit(b)} data-testid={`branch-edit-${b.id}`} className="rounded-lg bg-white/5 p-2 text-slate-300 hover:bg-white/10"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(b)} data-testid={`branch-delete-${b.id}`} className="rounded-lg bg-red-500/10 p-2 text-red-400 hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
          {!branches.length && <p className="px-5 py-10 text-center text-sm text-slate-500" data-testid="admin-branches-empty">No branches yet — add your first agent branch.</p>}
        </div>
      </div>
    </div>
  );
}
